import React, { useEffect, useState } from "react";
import { api, resolveImageUrl } from "@/lib/api";
import { useT } from "@/context/LangContext";
import { Sparkles, MapPin, ArrowRight, Crown } from "lucide-react";
import PartnerDetailDialog from "@/components/PartnerDetailDialog";

export default function PartnerOfMonth() {
  const { t } = useT();
  const [partner, setPartner] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/partner-of-month");
        if (alive) setPartner(data);
      } catch {
        // no active partner — section will hide
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!partner) return null;

  const unitValue = partner.offer_value || 0;
  const img = resolveImageUrl(partner.image_url);

  return (
    <section
      className="section-pad bg-navy-900 text-white relative overflow-hidden"
      data-testid="partner-of-month-section"
    >
      {/* grain / glow */}
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] bg-lime-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container-app relative">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 chip bg-lime-500 text-navy-900 mb-4" data-testid="potm-badge">
              <Crown className="w-3.5 h-3.5" /> {t("potm.badge")}
            </div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight">
              {t("potm.title")}
            </h2>
            <p className="text-white/70 mt-3 max-w-xl">{t("potm.subtitle")}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-8 items-stretch">
          {/* Image */}
          <div className="md:col-span-6 relative group">
            <div
              className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 shadow-2xl cursor-pointer"
              onClick={() => setOpen(true)}
              data-testid="potm-image"
            >
              <img
                src={img}
                alt={partner.name}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-lime-400 font-bold mb-1">
                    {partner.category}
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-black leading-tight" data-testid="potm-name">
                    {partner.name}
                  </h3>
                  <div className="flex items-center gap-1.5 text-white/70 text-sm mt-1">
                    <MapPin className="w-3.5 h-3.5" /> {partner.city || "Berchem-Sainte-Agathe"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Offer */}
          <div className="md:col-span-6 flex flex-col justify-between">
            <div>
              <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-sm">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-lime-400 shrink-0 mt-1" />
                  <p className="text-xl md:text-2xl font-semibold leading-snug" data-testid="potm-offer">
                    {partner.offer}
                  </p>
                </div>
                {partner.description && (
                  <p className="text-white/60 mt-4 text-sm leading-relaxed">
                    {partner.description}
                  </p>
                )}
              </div>

              {/* Saving block */}
              <div className="mt-6 flex items-center gap-4">
                <div className="bg-lime-500 text-navy-900 rounded-2xl px-6 py-4 font-heading font-black text-3xl md:text-4xl leading-none" data-testid="potm-saving">
                  −{Math.round(unitValue)}€
                </div>
                <div className="text-white/70 text-sm">
                  <div className="uppercase tracking-widest text-[11px] font-bold text-lime-400">
                    {t("potm.saving_label")}
                  </div>
                  <div>
                    <strong className="text-white">{Math.round(unitValue)}€</strong> {t("potm.per_month")}
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-8 inline-flex items-center gap-2 bg-lime-500 hover:bg-lime-400 text-navy-900 font-bold px-6 py-3.5 rounded-full transition w-fit"
              data-testid="potm-cta"
            >
              {t("potm.cta")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <PartnerDetailDialog partner={partner} open={open} onOpenChange={setOpen} />
    </section>
  );
}
